class ScaleToFitViewport {
    static targetSelector = '.js-scale-to-fit-viewport';
    static breakpoint = 768;
    static instanceProperty = '__scaleToFitViewportInstance';
    static viewportProperty = '__scaleToFitViewportElement';
    static overflowClassProperty = '__scaleToFitViewportHadOverflowXAuto';
    static instances = [];
    static listenersInitialized = false;
    static refreshQueued = false;

    constructor(targetElement) {
        this.targetElement = targetElement || null;
    }

    shouldScale() {
        return !!this.targetElement && window.innerWidth < ScaleToFitViewport.breakpoint;
    }

    getCachedViewportElement() {
        const viewportElement = this.targetElement
            ? this.targetElement[ScaleToFitViewport.viewportProperty]
            : null;

        if (!viewportElement || !document.body.contains(viewportElement)) {
            return null;
        }

        return viewportElement;
    }

    ensureViewportElement(createIfMissing = false) {
        const cachedViewportElement = this.getCachedViewportElement();
        if (cachedViewportElement) {
            return cachedViewportElement;
        }

        if (!this.targetElement) {
            return null;
        }

        const parentElement = this.targetElement.parentElement;
        if (!parentElement) {
            return null;
        }

        if (parentElement.getAttribute('data-scale-to-fit-generated') === '1') {
            this.targetElement[ScaleToFitViewport.viewportProperty] = parentElement;
            return parentElement;
        }

        if (!createIfMissing) {
            return null;
        }

        const generatedViewport = document.createElement('div');
        generatedViewport.setAttribute('data-scale-to-fit-generated', '1');
        generatedViewport.style.width = '100%';
        generatedViewport.style.maxWidth = '100%';

        parentElement.insertBefore(generatedViewport, this.targetElement);
        generatedViewport.appendChild(this.targetElement);

        this.targetElement[ScaleToFitViewport.viewportProperty] = generatedViewport;
        return generatedViewport;
    }

    removeGeneratedViewportElement() {
        const viewportElement = this.ensureViewportElement(false);

        if (!viewportElement || viewportElement.getAttribute('data-scale-to-fit-generated') !== '1' || !this.targetElement) {
            return;
        }

        if (viewportElement.parentNode) {
            viewportElement.parentNode.insertBefore(this.targetElement, viewportElement);
            viewportElement.parentNode.removeChild(viewportElement);
        }

        delete this.targetElement[ScaleToFitViewport.viewportProperty];
    }

    mount() {
        if (!this.targetElement) {
            return this;
        }

        this.targetElement[ScaleToFitViewport.instanceProperty] = this;
        if (ScaleToFitViewport.instances.indexOf(this) === -1) {
            ScaleToFitViewport.instances.push(this);
        }

        ScaleToFitViewport.initializeListeners();
        this.update();

        return this;
    }

    reset() {
        const viewportElement = this.ensureViewportElement(false);

        if (viewportElement) {
            viewportElement.style.height = '';
            viewportElement.style.overflow = '';
        }

        if (!this.targetElement) {
            return;
        }

        if (this.targetElement[ScaleToFitViewport.overflowClassProperty] === true) {
            this.targetElement.classList.add('overflow-x-auto');
            delete this.targetElement[ScaleToFitViewport.overflowClassProperty];
        }

        this.targetElement.style.width = '';
        this.targetElement.style.transform = '';
        this.targetElement.style.transformOrigin = '';
    }

    update() {
        if (!this.targetElement) {
            return;
        }

        this.reset();

        if (!this.shouldScale()) {
            this.removeGeneratedViewportElement();
            return;
        }

        const viewportElement = this.ensureViewportElement(true);
        if (!viewportElement) {
            return;
        }

        const availableWidth = viewportElement.clientWidth || 0;
        const naturalWidth = Math.max(
            this.targetElement.scrollWidth || 0,
            this.targetElement.offsetWidth || 0
        );

        if (!availableWidth || !naturalWidth || naturalWidth <= availableWidth) {
            return;
        }

        const scale = availableWidth / naturalWidth;
        if (!scale || scale >= 1) {
            return;
        }

        if (this.targetElement.classList.contains('overflow-x-auto')) {
            this.targetElement[ScaleToFitViewport.overflowClassProperty] = true;
            this.targetElement.classList.remove('overflow-x-auto');
        }

        this.targetElement.style.width = naturalWidth + 'px';
        this.targetElement.style.transform = 'scale(' + scale + ')';
        this.targetElement.style.transformOrigin = 'top left';

        const naturalHeight = this.targetElement.offsetHeight || 0;
        viewportElement.style.height = Math.ceil(naturalHeight * scale) + 'px';
        viewportElement.style.overflow = 'hidden';
    }

    static initializeListeners() {
        if (ScaleToFitViewport.listenersInitialized) {
            return;
        }

        ScaleToFitViewport.listenersInitialized = true;
        $(window).on('resize.scaleToFitViewport orientationchange.scaleToFitViewport', function () {
            ScaleToFitViewport.queueRefresh();
        });
    }

    static schedule(callback) {
        if (window.requestAnimationFrame) {
            window.requestAnimationFrame(callback);
            return;
        }

        window.setTimeout(callback, 0);
    }

    static queueRefresh() {
        if (ScaleToFitViewport.refreshQueued) {
            return;
        }

        ScaleToFitViewport.refreshQueued = true;
        ScaleToFitViewport.schedule(function () {
            ScaleToFitViewport.refreshQueued = false;
            ScaleToFitViewport.refreshAll();
        });
    }

    static clearTargetElementState(targetElement, instance) {
        if (!targetElement) {
            return;
        }

        if (!instance || targetElement[ScaleToFitViewport.instanceProperty] === instance) {
            delete targetElement[ScaleToFitViewport.instanceProperty];
        }

        delete targetElement[ScaleToFitViewport.viewportProperty];
    }

    static pruneDetachedInstances() {
        ScaleToFitViewport.instances = ScaleToFitViewport.instances.filter((instance) => {
            if (!instance.targetElement || !document.body.contains(instance.targetElement)) {
                ScaleToFitViewport.clearTargetElementState(instance.targetElement, instance);
                return false;
            }

            return true;
        });
    }

    static isTargetElement(element, selector = ScaleToFitViewport.targetSelector) {
        if (!element || element.nodeType !== 1 || element.getAttribute('data-scale-to-fit-generated') === '1') {
            return false;
        }

        if (typeof element.matches !== 'function') {
            return false;
        }

        return element.matches(selector);
    }

    static collectTargetElements(context, selector) {
        const elements = new Set();
        const roots = context && context.jquery ? context : $(context || document);

        roots.each(function () {
            const root = this;
            const rootElement = $(root);

            if (ScaleToFitViewport.isTargetElement(root, selector)) {
                elements.add(root);
            }

            rootElement.find(selector).each(function () {
                if (ScaleToFitViewport.isTargetElement(this, selector)) {
                    elements.add(this);
                }
            });
        });

        return Array.from(elements);
    }

    static mountAll(context = document, options = {}) {
        const selector = options.selector || ScaleToFitViewport.targetSelector;
        const elements = ScaleToFitViewport.collectTargetElements(context, selector);

        elements.forEach((element) => {
            let instance = element[ScaleToFitViewport.instanceProperty];

            if (!(instance instanceof ScaleToFitViewport)) {
                instance = new ScaleToFitViewport(element);
                instance.mount();
                return;
            }

            instance.update();
        });

        ScaleToFitViewport.pruneDetachedInstances();
        return elements.map((element) => element[ScaleToFitViewport.instanceProperty]).filter(Boolean);
    }

    static mount(context = document, options = {}) {
        ScaleToFitViewport.schedule(function () {
            ScaleToFitViewport.mountAll(context, options);
        });
    }

    static refreshAll() {
        ScaleToFitViewport.pruneDetachedInstances();
        ScaleToFitViewport.instances.forEach((instance) => {
            instance.update();
        });
    }
}

window.ScaleToFitViewport = ScaleToFitViewport;
